import { Button } from '@ui/button.tsx';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form, FormControl, FormField, FormItem, FormDescription } from '../ui/form.tsx';
import { Checkbox } from '../ui/checkbox.tsx';
import { useTranslation } from 'react-i18next';
import { queryClient } from '@r/utils/utils.ts';
import { Link, useRouter } from '@tanstack/react-router';
import { useGetConfigByType } from '@r/hooks/use-workspace-config.ts';
import { useUser } from '@r/api/auth.tsx';
import { useEffect } from 'react';
import { useProfileUpdate } from '@r/api/generated/client.ts';

export function TermsAndConditionsForm() {
  const legalNotice = useGetConfigByType('media', 'legal-notice')?.[0];
  const regulation = useGetConfigByType('media', 'regulation')?.[0];
  const privacyPolicy = useGetConfigByType('media', 'privacy-policy')?.[0];
  const router = useRouter();
  const user = useUser();

  useEffect(() => {
    if (user.data?.visibleWorkspaces[0].status === 'user.status.registered') {
      router.invalidate();
    }
  }, [user.data?.visibleWorkspaces[0].status]);

  const acceptTermAndConditionMutation = useProfileUpdate({
    mutation: {
      onSuccess: async () => {
        await queryClient.refetchQueries({ queryKey: ['workspaceConfigs'] });
        await queryClient.refetchQueries({ queryKey: ['availableRoutes'] });
        await user.refetch();
      }
    }
  });

  const FormSchema = z.object({
    acceptance: z.boolean().default(false)
  });

  const form = useForm<z.input<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      acceptance: false
    }
  });

  const { t } = useTranslation();

  function onSubmit() {
    acceptTermAndConditionMutation.mutate({ data: {} });
  }

  function createLink(text: string, url: string | undefined, title: string) {
    return url ? (
      <Link to={url} target={'_blank'} className="font-medium underline" title={title}>
        {text}
      </Link>
    ) : (
      text
    );
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="acceptance"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center space-x-7 space-y-0">
              <FormControl>
                <Checkbox
                  className="rounded-full border-foreground"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>

              <div className="space-y-1 leading-none">
                <FormDescription className="mt-0 font-light text-[0.8rem] text-foreground leading-6">
                  J’accepte les{' '}
                  {createLink(
                    'termes et conditions de l’opération',
                    legalNotice,
                    'Consultez les mentions légales'
                  )}
                  {'. '}Je confirme avoir lu, compris et accepté le{' '}
                  {createLink(
                    'règlement du programme',
                    regulation,
                    "Consultez le règlement et les conditions générales d'utilisation"
                  )}{' '}
                  ainsi que la{' '}
                  {createLink(
                    'politique de confidentialité',
                    privacyPolicy,
                    'Consultez la politique de confidentialité'
                  )}
                  {'.'}
                </FormDescription>
              </div>
            </FormItem>
          )}
        />

        <Button className="w-full" loading={acceptTermAndConditionMutation.isPending} type="submit">
          {t('terms.and.conditions.submit.button')}
        </Button>
      </form>
    </Form>
  );
}
